/**
 * Utilidades para restaurar segmentos de un proyecto guardado con deduplicación.
 * Evita que segmentos duplicados (mismo bounds + misma cantidad de módulos) se sumen.
 */

import type { ModuleSegment, SolarModule } from '../types/geotiff.types';

/** ID de polígono para segmentos free-field (no asociados a un techo). Debe coincidir con FREE_FIELD_POLYGON_ID del viewer. */
export const FREE_FIELD_POLYGON_ID = -1;

const COORD_PRECISION = 6;
const MIN_POLYGON_VERTICES = 3;
const DEFAULT_MODULE_SPACING = 0.02;
const DEFAULT_ROW_SPACING = 1.0;

/** Clave única por geometría + cantidad de módulos (para dedup). */
export function segmentDedupKey(
  latLngBounds: [number, number][],
  moduleCount: number
): string {
  const rounded = latLngBounds
    .map(([lat, lng]) => `${(lat ?? 0).toFixed(COORD_PRECISION)},${(lng ?? 0).toFixed(COORD_PRECISION)}`)
    .join('|');
  return `${rounded}_${moduleCount}`;
}

/** Forma mínima de un segmento tal como viene del API (load project / by-deal). */
export interface SegmentPayload {
  segment_id: number;
  snap_edge_roof_id?: number;
  bounds?: { coordinates?: number[][][] } | number[][];
  geometry?: { coordinates?: number[][][] };
  modules?: Array<{ position: number[]; width: number; height: number; orientation: string; rotation?: number }>;
  rotation?: number;
  module_config?: { spacing?: number; row_spacing_multiplier?: number };
}

/** Devuelve el anillo de coordenadas en formato API ([lng, lat][]) o undefined. */
function getRawBoundsRing(segment: SegmentPayload): number[][] | undefined {
  const b = segment.bounds;
  if (b == null) return segment.geometry?.coordinates?.[0];
  if (Array.isArray(b)) return b;
  return b.coordinates?.[0];
}

/** Extrae bounds en [lat, lng][] desde un segmento del API (v1 o v2). */
export function parseSegmentBounds(segment: SegmentPayload): [number, number][] | null {
  const raw = getRawBoundsRing(segment);
  if (!Array.isArray(raw) || raw.length < MIN_POLYGON_VERTICES) return null;
  const parsed = raw
    .filter((coord) => Array.isArray(coord) && coord[0] != null && coord[1] != null)
    .map((coord) => [coord[1], coord[0]] as [number, number]);
  return parsed.length >= MIN_POLYGON_VERTICES ? parsed : null;
}

export interface RestoreSegmentsResult {
  segmentsByRoofId: Record<number, ModuleSegment[]>;
  modulesByRoofId: Record<number, SolarModule[]>;
}

/**
 * Restaura segmentos desde el payload del proyecto aplicando deduplicación.
 * Si varios segmentos tienen el mismo bounds y misma cantidad de módulos, solo se conserva el primero.
 *
 * @param segments - Array de segmentos (loadedProjectData.segments o project.segments)
 * @param defaultRoofId - roofId cuando el segmento no tiene snap_edge_roof_id (ej. selectedPolygon o FREE_FIELD_POLYGON_ID)
 */
export function restoreSegmentsDeduplicated(
  segments: SegmentPayload[],
  defaultRoofId: number
): RestoreSegmentsResult {
  const segmentsByRoofId: Record<number, ModuleSegment[]> = {};
  const modulesByRoofId: Record<number, SolarModule[]> = {};
  const seenKeys = new Set<string>();

  for (const segment of segments) {
    const roofId =
      segment.snap_edge_roof_id !== undefined
        ? segment.snap_edge_roof_id
        : defaultRoofId;
    const isFreeField = roofId === FREE_FIELD_POLYGON_ID;

    const latLngBounds = parseSegmentBounds(segment);
    if (!latLngBounds || latLngBounds.length < MIN_POLYGON_VERTICES) continue;

    const segmentModules: SolarModule[] = [];
    if (segment.modules?.length) {
      for (const mod of segment.modules) {
        segmentModules.push({
          position: [mod.position[0], mod.position[1]] as [number, number],
          width: mod.width,
          height: mod.height,
          orientation: (mod.orientation === 'landscape' ? 'landscape' : 'portrait') as 'portrait' | 'landscape',
          rotation: mod.rotation,
          azimuth: mod.rotation,
          segmentId: segment.segment_id,
        });
      }
    }

    const key = segmentDedupKey(latLngBounds, segmentModules.length);
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);

    if (!segmentsByRoofId[roofId]) segmentsByRoofId[roofId] = [];
    segmentsByRoofId[roofId].push({
      id: segment.segment_id,
      bounds: latLngBounds,
      rotation: segment.rotation ?? 0,
      azimuth: segment.rotation ?? 0,
      modules: segmentModules,
      freeField: isFreeField,
      config: {
        moduleSpacing: segment.module_config?.spacing ?? DEFAULT_MODULE_SPACING,
        rowSpacing: segment.module_config?.row_spacing_multiplier ?? DEFAULT_ROW_SPACING,
        targetCount: null,
      },
    });

    if (!modulesByRoofId[roofId]) modulesByRoofId[roofId] = [];
    modulesByRoofId[roofId].push(...segmentModules);
  }

  return { segmentsByRoofId, modulesByRoofId };
}
